import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { vehicleCatalogService } from '../services/apiService';
import { useAuth } from '../auth/AuthContext';

const VehicleContext = createContext(null);

export const VehicleProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Bearer token (legacy) or cookie session (B-01B)
  const isAuth = useCallback(
    () => isAuthenticated || !!localStorage.getItem('authToken'),
    [isAuthenticated]
  );

  // Fetch all vehicles from backend
  const fetchVehicles = useCallback(async () => {
    if (!isAuth()) {
      // Guests should not see saved vehicle cards
      setVehicles([]);
      setSelectedVehicle(null);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/user/vehicles');
      const list = res.data?.data || [];
      setVehicles(list);
      const serverActive = list.find((v) => v.isDefault || v.isActive);
      setSelectedVehicle(serverActive || list[0] || null);
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuth]);

  // On mount / auth change: load vehicles
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles, isAuthenticated]);

  // Sync selectedVehicle to localStorage
  useEffect(() => {
    if (selectedVehicle) {
      localStorage.setItem('selectedVehicle', JSON.stringify(selectedVehicle));
    } else {
      localStorage.removeItem('selectedVehicle');
    }
  }, [selectedVehicle]);

  const addVehicle = useCallback(async (vehicleData) => {
    if (!isAuth()) {
      return null;
    }
    try {
      setLoading(true);
      const res = await api.post('/vehicles', vehicleData);
      const newV = res.data?.data;
      const newId = newV?._id || newV?.id;
      if (newId) {
        try {
          await api.put(`/vehicles/${newId}/select`);
        } catch (selectErr) {
          console.warn('Failed to mark new vehicle as selected:', selectErr);
        }
      }
      await fetchVehicles();
      if (newV) {
        setSelectedVehicle(newV);
      }
      return newV;
    } catch (err) {
      console.error('API addVehicle failed, saving locally:', err);
      // Fallback to localStorage when backend is unreachable
      const newV = { id: Date.now(), ...vehicleData, isDefault: vehicles.length === 0, createdAt: new Date().toISOString() };
      const updated = [...vehicles, newV];
      setVehicles(updated);
      localStorage.setItem('vehicles', JSON.stringify(updated));
      if (newV.isDefault) setSelectedVehicle(newV);
      return newV;
    } finally {
      setLoading(false);
    }
  }, [fetchVehicles]);

  const removeVehicle = useCallback(async (vehicleId) => {
    if (!isAuth()) {
      return;
    }
    try {
      setLoading(true);
      await api.delete(`/vehicles/${vehicleId}`);
      await fetchVehicles();
      if (selectedVehicle?._id === vehicleId || selectedVehicle?.id === vehicleId) {
        const remaining = vehicles.filter(v => (v._id || v.id) !== vehicleId);
        setSelectedVehicle(remaining[0] || null);
      }
    } catch (err) {
      console.error('API removeVehicle failed, removing locally:', err);
      const updated = vehicles.filter(v => (v._id || v.id) !== vehicleId);
      setVehicles(updated);
      localStorage.setItem('vehicles', JSON.stringify(updated));
      if (selectedVehicle?._id === vehicleId || selectedVehicle?.id === vehicleId) {
        setSelectedVehicle(updated[0] || null);
      }
    } finally {
      setLoading(false);
    }
  }, [vehicles, selectedVehicle, fetchVehicles]);

  const selectVehicle = useCallback(async (vehicle) => {
    if (!isAuth()) return;
    setSelectedVehicle(vehicle);
    const id = vehicle._id || vehicle.id;
    try {
      await api.put(`/vehicles/${id}/select`);
      await fetchVehicles();
    } catch (err) {
      console.error('Failed to select vehicle:', err);
    }
  }, [fetchVehicles]);

  const clearSelectedVehicle = useCallback(() => {
    setSelectedVehicle(null);
  }, []);

  const updateVehicle = useCallback(async (vehicleId, vehicleData) => {
    if (!isAuth()) {
      return;
    }
    try {
      setLoading(true);
      await api.put(`/vehicles/${vehicleId}`, vehicleData);
      await fetchVehicles();
    } catch (err) {
      console.error('API updateVehicle failed, updating locally:', err);
      const updated = vehicles.map(v => ((v._id || v.id) === vehicleId ? { ...v, ...vehicleData } : v));
      setVehicles(updated);
      localStorage.setItem('vehicles', JSON.stringify(updated));
    } finally {
      setLoading(false);
    }
  }, [fetchVehicles]);

  // Lookup vehicle data dropdowns
  const fetchMakes = useCallback(async () => {
    try {
      const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
      const list = Array.isArray(makers) ? makers : [];
      return list
        .map((maker) => {
          const id = maker?._id || maker?.id;
          const name = String(maker?.name || '').trim();
          if (!id || !name) return null;
          return { value: name, label: name, makerId: String(id) };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch (err) {
      console.error('Failed to fetch makes:', err);
      return [];
    }
  }, []);

  const fetchModels = useCallback(async (make) => {
    try {
      const makeName = String(make || '').trim().toLowerCase();
      if (!makeName) return [];

      const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
      const listMakers = Array.isArray(makers) ? makers : [];
      const maker = listMakers.find(
        (m) => String(m?.name || '').trim().toLowerCase() === makeName
      );
      const makerId = maker?._id || maker?.id;
      if (!makerId) return [];

      const models = await vehicleCatalogService.listModelsPublic(makerId, {
        includeInactive: true
      });
      const list = Array.isArray(models) ? models : [];
      return list
        .map((model) => {
          const id = model?._id || model?.id;
          const name = String(model?.name || '').trim();
          if (!id || !name) return null;
          return { value: name, label: name, modelId: String(id) };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch (err) {
      console.error('Failed to fetch models:', err);
      return [];
    }
  }, []);

  const fetchYears = useCallback(async (make, model, options = {}) => {
    try {
      const modelIdFromOptions = String(options?.modelId || '').trim();
      const makeName = String(make || '').trim().toLowerCase();
      const modelName = String(model || '').trim().toLowerCase();
      if (!modelIdFromOptions && (!makeName || !modelName)) return [];

      let modelId = modelIdFromOptions;
      if (!modelId) {
        const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
        const listMakers = Array.isArray(makers) ? makers : [];
        const maker = listMakers.find(
          (m) => String(m?.name || '').trim().toLowerCase() === makeName
        );
        const makerId = maker?._id || maker?.id;
        if (!makerId) return [];

        const models = await vehicleCatalogService.listModelsPublic(makerId, {
          includeInactive: true
        });
        const listModels = Array.isArray(models) ? models : [];
        const selectedModel = listModels.find(
          (m) => String(m?.name || '').trim().toLowerCase() === modelName
        );
        modelId = String(selectedModel?._id || selectedModel?.id || '');
      }
      if (!modelId) return [];

      const trims = await vehicleCatalogService.listTrimsPublic(modelId, {
        includeInactive: true
      });
      const years = [...new Set(
        (Array.isArray(trims) ? trims : [])
          .map((t) => Number(t?.year))
          .filter((y) => Number.isFinite(y))
      )].sort((a, b) => b - a);

      return years.map((year) => ({ value: String(year), label: String(year) }));
    } catch (err) {
      console.error('Failed to fetch years:', err);
      return [];
    }
  }, []);

  const fetchVariants = useCallback(async (make, model, year = '', options = {}) => {
    try {
      const modelIdFromOptions = String(options?.modelId || '').trim();
      const makeName = String(make || '').trim().toLowerCase();
      const modelName = String(model || '').trim().toLowerCase();
      const selectedYear = String(year || '').trim();
      if (!modelIdFromOptions && (!makeName || !modelName)) return [];

      let modelId = modelIdFromOptions;
      if (!modelId) {
        const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
        const listMakers = Array.isArray(makers) ? makers : [];
        const maker = listMakers.find(
          (m) => String(m?.name || '').trim().toLowerCase() === makeName
        );
        const makerId = maker?._id || maker?.id;
        if (!makerId) return [];

        const models = await vehicleCatalogService.listModelsPublic(makerId, {
          includeInactive: true
        });
        const listModels = Array.isArray(models) ? models : [];
        const selectedModel = listModels.find(
          (m) => String(m?.name || '').trim().toLowerCase() === modelName
        );
        modelId = String(selectedModel?._id || selectedModel?.id || '');
      }
      if (!modelId) return [];

      const trims = await vehicleCatalogService.listTrimsPublic(modelId, {
        includeInactive: true
      });
      const variants = [...new Set(
        (Array.isArray(trims) ? trims : [])
          .filter((t) => !selectedYear || String(t?.year || '') === selectedYear)
          .map((t) => String(t?.variant || '').trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));

      return variants.map((variant) => ({ value: variant, label: variant }));
    } catch (err) {
      console.error('Failed to fetch variants:', err);
      return [];
    }
  }, []);

  const lookupByRegistration = useCallback(async (registrationNumber) => {
    const reg = String(registrationNumber || '').trim();
    if (!reg) return null;

    const normalized = reg.toUpperCase().replace(/\s+/g, '');
    const savedMatch = vehicles.find(
      (v) =>
        String(v.registrationNumber || v.regNo || '')
          .toUpperCase()
          .replace(/\s+/g, '') === normalized
    );
    if (savedMatch) return savedMatch;

    try {
      const res = await api.get('/vehicles/lookup/registration', {
        params: { registrationNumber: reg, country: 'IN' },
      });
      const vehicle = res.data?.data?.vehicle;
      if (!vehicle || (!vehicle.make && !vehicle.model)) return null;
      return vehicle;
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      console.error('Registration lookup failed:', message || err);
      if (status === 503) {
        const lookupErr = new Error(
          'Registration lookup is not configured. Use Add New Vehicle or ask admin to enable the number-plate API.'
        );
        lookupErr.code = 'NUMBER_PLATE_DISABLED';
        throw lookupErr;
      }
      return null;
    }
  }, [vehicles]);

  const lookupByVIN = useCallback((vin) => {
    const value = String(vin || '').trim().toUpperCase();
    if (!value) return null;
    return (
      vehicles.find((v) => String(v.vin || '').trim().toUpperCase() === value) || null
    );
  }, [vehicles]);

  const searchByFilters = useCallback((filters = {}) => {
    const { make, model, variant, year, fuelType } = filters;
    let list = [...vehicles];

    if (make) {
      const m = String(make).trim().toLowerCase();
      list = list.filter((v) => String(v.make || '').trim().toLowerCase() === m);
    }
    if (model) {
      const m = String(model).trim().toLowerCase();
      list = list.filter((v) => String(v.model || '').trim().toLowerCase() === m);
    }
    if (variant) {
      const v = String(variant).trim().toLowerCase();
      list = list.filter((item) => String(item.variant || '').trim().toLowerCase().includes(v));
    }
    if (year) {
      list = list.filter((item) => String(item.year || '') === String(year).trim());
    }
    if (fuelType) {
      const f = String(fuelType).trim().toLowerCase();
      list = list.filter((item) => String(item.fuelType || '').trim().toLowerCase() === f);
    }

    return list;
  }, [vehicles]);

  const fetchMonths = useCallback(async (make, model, year, options = {}) => {
    try {
      const modelIdFromOptions = String(options?.modelId || '').trim();
      const makeName = String(make || '').trim().toLowerCase();
      const modelName = String(model || '').trim().toLowerCase();
      const selectedYear = String(year || '').trim();
      if (!selectedYear || (!modelIdFromOptions && (!makeName || !modelName))) return [];

      let modelId = modelIdFromOptions;
      if (!modelId) {
        const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
        const listMakers = Array.isArray(makers) ? makers : [];
        const maker = listMakers.find(
          (m) => String(m?.name || '').trim().toLowerCase() === makeName
        );
        const makerId = maker?._id || maker?.id;
        if (!makerId) return [];

        const models = await vehicleCatalogService.listModelsPublic(makerId, {
          includeInactive: true
        });
        const listModels = Array.isArray(models) ? models : [];
        const selectedModel = listModels.find(
          (m) => String(m?.name || '').trim().toLowerCase() === modelName
        );
        modelId = String(selectedModel?._id || selectedModel?.id || '');
      }
      if (!modelId) return [];

      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const trims = await vehicleCatalogService.listTrimsPublic(modelId, {
        includeInactive: true
      });
      const monthNums = [...new Set(
        (Array.isArray(trims) ? trims : [])
          .filter((t) => String(t?.year || '') === selectedYear)
          .map((t) => Number(t?.month))
          .filter((m) => Number.isFinite(m) && m >= 1 && m <= 12)
      )].sort((a, b) => a - b);

      return monthNums.map((m) => ({ value: String(m), label: monthNames[m - 1] || String(m) }));
    } catch (err) {
      console.error('Failed to fetch months:', err);
      return [];
    }
  }, []);

  const value = {
    vehicles,
    selectedVehicle,
    loading,
    addVehicle,
    removeVehicle,
    updateVehicle,
    selectVehicle,
    clearSelectedVehicle,
    fetchVehicles,
    fetchMakes,
    fetchModels,
    fetchYears,
    fetchMonths,
    fetchVariants,
    lookupByRegistration,
    lookupByVIN,
    searchByFilters,
  };

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
};

export const useVehicle = () => {
  const context = useContext(VehicleContext);
  if (!context) throw new Error('useVehicle must be used within a VehicleProvider');
  return context;
};
